import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import type { BulkQuoteBatchStatus } from "../schemas/bulk-quote-batch-status-schema.ts";
import { bulkQuoteBatchStatusSchema } from "../schemas/bulk-quote-batch-status-schema.ts";
import type { ActiveBulkQuoteBatch } from "../state/active-bulk-quote-batch-state.ts";
import {
	activeBulkQuoteBatchAtom,
	createActiveBulkQuoteBatch,
	isBulkQuoteBatchForCustomer,
	isBulkQuoteBatchOwnedBy,
	resolveBulkQuoteBatchOwnerId,
} from "../state/active-bulk-quote-batch-state.ts";

export const BULK_QUOTE_BATCH_POLL_INTERVAL_MS = 1000;
export const BULK_QUOTE_BATCH_SLOW_POLL_INTERVAL_MS = 5000;
export const BULK_QUOTE_BATCH_FAST_POLL_DURATION_MS = 10_000;
export const BULK_QUOTE_BATCH_POLL_TIMEOUT_MS = 60_000;
export const BULK_QUOTE_BATCH_MAX_POLL_DURATION_MS = 5 * 60_000;
export const BULK_QUOTE_BATCH_REQUEST_TIMEOUT_MS = 10_000;

const terminalStatuses = new Set<BulkQuoteBatchStatus["status"]>([
	"completed",
	"completed_with_errors",
	"failed",
]);

export const isBulkQuoteBatchTerminalStatus = (
	status: BulkQuoteBatchStatus["status"] | undefined,
): boolean => status != null && terminalStatuses.has(status);

const getBulkQuoteBatchStatusQueryKey = (bulkQuoteId: string | null) =>
	["bulk-quote-batch-status", bulkQuoteId] as const;

const getFocusedOwnedBatch = (
	focusedBatchId: string | null,
	ownedBatch: ActiveBulkQuoteBatch | null,
): ActiveBulkQuoteBatch | null => {
	if (
		focusedBatchId == null ||
		ownedBatch == null ||
		focusedBatchId !== ownedBatch.bulkQuoteId
	) {
		return null;
	}

	return ownedBatch;
};

const resolveBulkQuoteBatchId = ({
	ownerId,
	focusedBatchId,
	focusedOwnedBatch,
	visibleBatch,
}: {
	ownerId: string | null;
	focusedBatchId: string | null;
	focusedOwnedBatch: ActiveBulkQuoteBatch | null;
	visibleBatch: ActiveBulkQuoteBatch | null;
}): string | null => {
	if (ownerId == null) {
		return null;
	}

	if (focusedBatchId == null) {
		return visibleBatch?.bulkQuoteId ?? null;
	}

	return focusedOwnedBatch != null && visibleBatch == null
		? null
		: focusedBatchId;
};

export const useBulkQuoteBatchStatus = (bulkQuoteId: string | null) => {
	const queryClient = useQueryClient();
	const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
	const [pollingStartedAt, setPollingStartedAt] = useState(() => Date.now());
	const resetPolling = useCallback(() => {
		setPollingStartedAt(Date.now());
		setIsPollingTimedOut(false);
	}, []);

	useEffect(() => {
		setPollingStartedAt(Date.now());
		setIsPollingTimedOut(false);
	}, [bulkQuoteId]);

	const pausePolling = useCallback(() => {
		setIsPollingTimedOut(true);
		void queryClient.cancelQueries({
			queryKey: getBulkQuoteBatchStatusQueryKey(bulkQuoteId),
		});
	}, [bulkQuoteId, queryClient]);

	const query = useQuery({
		queryKey: getBulkQuoteBatchStatusQueryKey(bulkQuoteId),
		queryFn: async ({ signal }): Promise<BulkQuoteBatchStatus> => {
			if (!bulkQuoteId) {
				throw new Error("A bulk quote ID is required to fetch batch status.");
			}

			const result = await getAndValidate(
				`${apiRoutes.BULKUPLOAD}/${bulkQuoteId}`,
				bulkQuoteBatchStatusSchema,
				{
					signal,
					timeout: BULK_QUOTE_BATCH_REQUEST_TIMEOUT_MS,
				},
			);
			if (!result.success) {
				throw new Error("Failed to fetch bulk quote batch status.");
			}

			return result.data;
		},
		enabled: bulkQuoteId != null && !isPollingTimedOut,
		refetchInterval: (currentQuery) => {
			if (isBulkQuoteBatchTerminalStatus(currentQuery.state.data?.status)) {
				return false;
			}

			const pollingDuration = Date.now() - pollingStartedAt;
			return pollingDuration < BULK_QUOTE_BATCH_FAST_POLL_DURATION_MS
				? BULK_QUOTE_BATCH_POLL_INTERVAL_MS
				: BULK_QUOTE_BATCH_SLOW_POLL_INTERVAL_MS;
		},
		refetchIntervalInBackground: true,
		retry: false,
		staleTime: 0,
		gcTime: 0,
	});

	const hasTerminalStatus = isBulkQuoteBatchTerminalStatus(query.data?.status);

	useEffect(() => {
		if (bulkQuoteId == null || isPollingTimedOut || hasTerminalStatus) {
			return;
		}

		const timeoutId = globalThis.setTimeout(() => {
			pausePolling();
		}, BULK_QUOTE_BATCH_POLL_TIMEOUT_MS);

		return () => globalThis.clearTimeout(timeoutId);
	}, [
		bulkQuoteId,
		hasTerminalStatus,
		isPollingTimedOut,
		pausePolling,
		pollingStartedAt,
		query.data?.processedCount,
	]);

	useEffect(() => {
		if (bulkQuoteId == null || isPollingTimedOut || hasTerminalStatus) {
			return;
		}

		const remainingPollingTime = Math.max(
			0,
			BULK_QUOTE_BATCH_MAX_POLL_DURATION_MS - (Date.now() - pollingStartedAt),
		);
		const timeoutId = globalThis.setTimeout(pausePolling, remainingPollingTime);

		return () => globalThis.clearTimeout(timeoutId);
	}, [
		bulkQuoteId,
		hasTerminalStatus,
		isPollingTimedOut,
		pausePolling,
		pollingStartedAt,
	]);

	return { ...query, isPollingTimedOut, resetPolling };
};

type UseBulkQuoteBatchBoardStatusOptions = {
	focusedBatchId: string | null;
	refetch: () => unknown;
	showError: (message: string) => unknown;
	showWarning: (message: string) => unknown;
};

export const useBulkQuoteBatchBoardStatus = ({
	focusedBatchId,
	refetch,
	showError,
	showWarning,
}: UseBulkQuoteBatchBoardStatusOptions) => {
	const [activeBatch, setActiveBatch] = useAtom(activeBulkQuoteBatchAtom);
	const { employeeId, userGuid, email } = useIsAuthenticated();
	const { isInternal } = useDeploymentType();
	const selectedCustomer = useAtomValue(selectedCustomerAtom);
	const ownerId = resolveBulkQuoteBatchOwnerId({
		employeeId,
		userGuid,
		email,
	});
	// Mirrors the customer the fetch client attaches to external requests, so the
	// batch is tracked against the profile whose headers the status poll carries.
	const customerId =
		isInternal || selectedCustomer?.id == null
			? null
			: String(selectedCustomer.id);

	// Session storage survives logout, so a batch left by a previous sign-in must
	// be discarded rather than restored for whoever signs in next.
	const isOwnedByCurrentUser = isBulkQuoteBatchOwnedBy(activeBatch, ownerId);
	useEffect(() => {
		if (activeBatch == null || ownerId == null || isOwnedByCurrentUser) {
			return;
		}

		setActiveBatch(null);
	}, [activeBatch, isOwnedByCurrentUser, ownerId, setActiveBatch]);

	const ownedBatch = isOwnedByCurrentUser ? activeBatch : null;
	// A batch from another customer profile is kept in storage but neither polled
	// nor rendered: its status endpoint rejects the active customer headers, which
	// would otherwise surface an error toast and freeze the banner on stale counts
	// until the user switched back.
	const visibleBatch = isBulkQuoteBatchForCustomer(ownedBatch, customerId)
		? ownedBatch
		: null;
	const focusedOwnedBatch = getFocusedOwnedBatch(focusedBatchId, ownedBatch);

	// A newly uploaded batch always replaces whatever the board was tracking.
	useEffect(() => {
		if (
			focusedBatchId == null ||
			ownerId == null ||
			focusedOwnedBatch != null
		) {
			return;
		}

		setActiveBatch(
			createActiveBulkQuoteBatch(focusedBatchId, ownerId, customerId),
		);
	}, [customerId, focusedBatchId, focusedOwnedBatch, ownerId, setActiveBatch]);

	// Without a resolved identity the batch cannot be attributed, so nothing is
	// polled or rendered until sign-in settles.
	const resolvedBatchId = resolveBulkQuoteBatchId({
		ownerId,
		focusedBatchId,
		focusedOwnedBatch,
		visibleBatch,
	});
	const activeBatchId = visibleBatch?.bulkQuoteId ?? null;
	const isDismissed =
		activeBatchId === resolvedBatchId && visibleBatch?.isDismissed === true;
	const trackedBatchId = isDismissed ? null : resolvedBatchId;

	const statusQuery = useBulkQuoteBatchStatus(trackedBatchId);
	const { refetch: refetchStatus, resetPolling } = statusQuery;
	// Guard against cache bleed: only surface data that belongs to this batch.
	const batchStatus =
		trackedBatchId != null && statusQuery.data?.bulkQuoteId === trackedBatchId
			? statusQuery.data
			: undefined;
	const status = batchStatus?.status;
	const processedCount = batchStatus?.processedCount;
	const requestCount = batchStatus?.requestCount;
	const errorCount = batchStatus?.errorCount;

	useEffect(() => {
		if (trackedBatchId == null || ownerId == null || status == null) {
			return;
		}

		setActiveBatch((current) => ({
			bulkQuoteId: trackedBatchId,
			ownerId,
			customerId,
			status,
			processedCount: processedCount ?? null,
			requestCount: requestCount ?? null,
			errorCount: errorCount ?? null,
			notifiedStatus:
				current?.bulkQuoteId === trackedBatchId && current.status === status
					? current.notifiedStatus
					: null,
			isDismissed: false,
		}));
	}, [
		customerId,
		errorCount,
		ownerId,
		processedCount,
		requestCount,
		setActiveBatch,
		status,
		trackedBatchId,
	]);

	useEffect(() => {
		if (isBulkQuoteBatchTerminalStatus(status)) {
			refetch();
		}
	}, [refetch, status]);

	useEffect(() => {
		if (statusQuery.isError) {
			showError("Unable to check bulk upload processing status.");
		}
	}, [showError, statusQuery.isError]);

	const notifiedStatus =
		activeBatchId === trackedBatchId
			? (visibleBatch?.notifiedStatus ?? null)
			: null;

	useEffect(() => {
		if (
			trackedBatchId == null ||
			status == null ||
			!isBulkQuoteBatchTerminalStatus(status) ||
			status === notifiedStatus
		) {
			return;
		}

		if (status === "completed_with_errors") {
			showWarning(
				"Bulk upload completed with errors. Review the affected shipments on the Quote Board.",
			);
		} else if (status === "failed") {
			showError(
				"Bulk upload failed. Review the upload errors on the Quote Board.",
			);
		}

		setActiveBatch((current) =>
			current?.bulkQuoteId === trackedBatchId
				? { ...current, notifiedStatus: status }
				: current,
		);
	}, [
		notifiedStatus,
		setActiveBatch,
		showError,
		showWarning,
		status,
		trackedBatchId,
	]);

	const isPollingTimedOut =
		trackedBatchId != null && statusQuery.isPollingTimedOut;

	useEffect(() => {
		if (isPollingTimedOut) {
			showWarning(
				"Bulk upload processing is taking longer than expected. Refresh the Quote Board to check again.",
			);
		}
	}, [isPollingTimedOut, showWarning]);

	const displayedBatch = useMemo<ActiveBulkQuoteBatch | null>(() => {
		// Only live status is rendered. The persisted snapshot still drives which
		// batch is polled, but painting it would surface counts captured before the
		// user left the profile — the "2 of 6" a finished batch no longer has.
		if (trackedBatchId == null || ownerId == null || status == null) {
			return null;
		}

		return {
			bulkQuoteId: trackedBatchId,
			ownerId,
			customerId,
			status,
			processedCount: processedCount ?? null,
			requestCount: requestCount ?? null,
			errorCount: errorCount ?? null,
			notifiedStatus,
			isDismissed: false,
		};
	}, [
		customerId,
		errorCount,
		notifiedStatus,
		ownerId,
		processedCount,
		requestCount,
		status,
		trackedBatchId,
	]);

	const dismissBatch = useCallback(() => {
		setActiveBatch((current) =>
			isBulkQuoteBatchOwnedBy(current, ownerId) &&
			isBulkQuoteBatchForCustomer(current, customerId) &&
			current != null
				? { ...current, isDismissed: true }
				: current,
		);
	}, [customerId, ownerId, setActiveBatch]);

	const retryBatchStatus = useCallback(() => {
		// No batch is being tracked, so there is nothing to poll. Skipping avoids
		// force-refetching the disabled status query (which would throw on a null
		// batch id and surface a spurious error toast).
		if (trackedBatchId == null) {
			return;
		}
		resetPolling();
		void refetchStatus();
	}, [refetchStatus, resetPolling, trackedBatchId]);

	return {
		...statusQuery,
		data: batchStatus,
		activeBatch: displayedBatch,
		trackedBatchId,
		isPollingTimedOut,
		dismissBatch,
		retryBatchStatus,
	};
};
